import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

interface UserStatistics {
    _id: string;
    username?: string;
    fullName?: string;
    role: string;
    status?: string;
    hasApiKey: boolean;
    orderCount: number;
    paymentCount: number;
    paidTotal: number;
    unpaidTotal: number;
    balance: number;
}

interface StatisticsResponse {
    data: UserStatistics[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

interface UserDetail {
    _id: string;
    username?: string;
    fullName?: string;
    role: string;
    email?: string;
    phone?: string;
    status?: string;
    loginHistory?: Array<{
        ipAddress: string;
        userAgent: string;
        success: boolean;
        loggedAt: string;
    }>;
}

interface UserDetailResponse {
    user: UserDetail;
    orders: Array<Record<string, any>>;
    transactions: Array<Record<string, any>>;
    balance: number;
}

const formatAmount = (amount: number) => `${Number(amount || 0).toLocaleString('vi-VN')} đ`;

const roleLabel: Record<string, string> = {
    user: 'Thành viên',
    partner: 'Đối tác',
    admin: 'Quản trị viên',
};

const statusLabel: Record<string, string> = {
    CANCELED: 'Đã hủy',
    pending: 'Đang chờ',
    pending_admin: 'Chờ duyệt',
    processing: 'Đang xử lý',
    success: 'Thành công',
    failed: 'Thất bại',
    cancelled: 'Đã hủy',
    active: 'Hoạt động',
    inactive: 'Bị khóa',
};

const transactionTypeLabel: Record<string, string> = {
    deposit: 'Nạp tiền',
    payment: 'Thanh toán',
    withdraw: 'Rút tiền',
    refund: 'Hoàn tiền',
    bonus: 'Tiền thưởng',
};

const AdminUserStatisticsPage: React.FC = () => {
    const [statistics, setStatistics] = useState<UserStatistics[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState<UserDetailResponse | null>(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [unlockingUserId, setUnlockingUserId] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [partnerPaymentUser, setPartnerPaymentUser] = useState<UserStatistics | null>(null);
    const [isRecordingPartnerPayment, setIsRecordingPartnerPayment] = useState(false);
    const [partnerPaymentError, setPartnerPaymentError] = useState('');
    const [walletAdjustmentUser, setWalletAdjustmentUser] = useState<UserStatistics | null>(null);
    const [walletAdjustmentAction, setWalletAdjustmentAction] = useState<'credit' | 'debit'>('credit');
    const [walletAdjustmentAmount, setWalletAdjustmentAmount] = useState('');
    const [walletAdjustmentReason, setWalletAdjustmentReason] = useState('');
    const [walletAdjustmentError, setWalletAdjustmentError] = useState('');
    const [isAdjustingWallet, setIsAdjustingWallet] = useState(false);
    const [partnerApiKeyBusyUserId, setPartnerApiKeyBusyUserId] = useState<string | null>(null);
    const [newPartnerApiKey, setNewPartnerApiKey] = useState<string | null>(null);
    const [partnerPayment, setPartnerPayment] = useState({
        bankName: '',
        bankAccountName: '',
        bankAccount: '',
        amount: '',
    });
    const [newUser, setNewUser] = useState({
        username: '',
        fullName: '',
        email: '',
        phone: '',
        password: '',
        role: 'user',
    });
    const [error, setError] = useState('');

    const loadStatistics = async () => {
        setIsLoading(true);
        setError('');
        try {
            const query = new URLSearchParams({ page: String(page) });
            if (search) query.set('search', search);
            const result = await apiService.get<StatisticsResponse>(`/users/statistics?${query.toString()}`);
            setStatistics(result?.data || []);
            setTotalPages(result?.totalPages || 1);
        } catch {
            setError('Không tải được thống kê người dùng.');
        } finally {
            setIsLoading(false);
        }
    };

    const recordPartnerPayment = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!partnerPaymentUser) return;

        setIsRecordingPartnerPayment(true);
        setPartnerPaymentError('');
        const payload: Record<string, string | number> = {
            userId: partnerPaymentUser._id,
            bankName: partnerPayment.bankName.trim(),
            bankAccountName: partnerPayment.bankAccountName.trim(),
            bankAccount: partnerPayment.bankAccount.trim(),
        };
        if (partnerPayment.amount.trim()) payload.amount = Number(partnerPayment.amount);

        try {
            await apiService.post('/transactions/admin/partner-payment', payload);
            const userId = partnerPaymentUser._id;
            setPartnerPaymentUser(null);
            setPartnerPayment({ bankName: '', bankAccountName: '', bankAccount: '', amount: '' });
            await loadStatistics();
            if (selectedUser?.user._id === userId) await loadDetail(userId);
        } catch {
            setPartnerPaymentError('Không thể ghi nhận thanh toán. Vui lòng kiểm tra thông tin và thử lại.');
        } finally {
            setIsRecordingPartnerPayment(false);
        }
    };

    const managePartnerApiKey = async (user: UserStatistics, action: 'create' | 'revoke') => {
        if (action === 'revoke' && !window.confirm(`Thu hồi API key của ${user.username || user.fullName || 'Partner'}?`)) return;

        setPartnerApiKeyBusyUserId(user._id);
        setError('');
        try {
            if (action === 'create') {
                const result = await apiService.post<{ data: { apiKey: string } }>(`/users/${user._id}/api-key`);
                setNewPartnerApiKey(result.data.apiKey);
            } else {
                await apiService.delete(`/users/${user._id}/api-key`);
                setNewPartnerApiKey(null);
            }
            setStatistics((current) => current.map((item) => (
                item._id === user._id ? { ...item, hasApiKey: action === 'create' } : item
            )));
        } catch {
            setError(action === 'create' ? 'Không thể tạo API key cho Partner.' : 'Không thể thu hồi API key của Partner.');
        } finally {
            setPartnerApiKeyBusyUserId(null);
        }
    };

    const openWalletAdjustment = (user: UserStatistics, action: 'credit' | 'debit') => {
        setWalletAdjustmentUser(user);
        setWalletAdjustmentAction(action);
        setWalletAdjustmentAmount('');
        setWalletAdjustmentReason('');
        setWalletAdjustmentError('');
    };

    const adjustUserWallet = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!walletAdjustmentUser) return;
        const amount = Number(walletAdjustmentAmount);
        if (!Number.isSafeInteger(amount) || amount <= 0) {
            setWalletAdjustmentError('Số tiền phải là số nguyên dương.');
            return;
        }
        if (walletAdjustmentReason.trim().length < 5 || walletAdjustmentReason.trim().length > 500) {
            setWalletAdjustmentError('Lý do phải có từ 5 đến 500 ký tự.');
            return;
        }

        setIsAdjustingWallet(true);
        setWalletAdjustmentError('');
        try {
            await apiService.post('/transactions/admin/wallet-adjustment', {
                userId: walletAdjustmentUser._id,
                action: walletAdjustmentAction,
                amount,
                reason: walletAdjustmentReason.trim(),
            });
            const userId = walletAdjustmentUser._id;
            setWalletAdjustmentUser(null);
            await loadStatistics();
            if (selectedUser?.user._id === userId) await loadDetail(userId);
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setWalletAdjustmentError(Array.isArray(responseMessage)
                ? responseMessage.join(', ')
                : responseMessage || 'Không thể điều chỉnh số dư ví.');
        } finally {
            setIsAdjustingWallet(false);
        }
    };

    const loadDetail = async (userId: string) => {
        setIsDetailLoading(true);
        try {
            setSelectedUser(await apiService.get<UserDetailResponse>(`/users/statistics/${userId}`));
        } catch {
            setError('Không tải được chi tiết người dùng.');
        } finally {
            setIsDetailLoading(false);
        }
    };

    const unlockUser = async (user: UserStatistics) => {
        if (user.status !== 'inactive' || user.role === 'admin') return;
        if (!window.confirm(`Xác nhận mở khóa tài khoản ${user.username || user.fullName || ''}?`)) return;

        setUnlockingUserId(user._id);
        setError('');
        try {
            await apiService.post('/users/change-status', {
                userId: user._id,
                status: 'active',
            });
            await loadStatistics();
            if (selectedUser?.user._id === user._id) {
                await loadDetail(user._id);
            }
        } catch {
            setError('Không thể mở khóa tài khoản. Vui lòng thử lại.');
        } finally {
            setUnlockingUserId(null);
        }
    };

    const blockUser = async (user: UserStatistics) => {
        if (user.status === 'inactive' || user.role === 'admin') return;
        if (!window.confirm(`Xác nhận chặn tài khoản ${user.username || user.fullName || ''}?`)) return;

        setUnlockingUserId(user._id);
        setError('');
        try {
            await apiService.post('/users/change-status', {
                userId: user._id,
                status: 'inactive',
            });
            await loadStatistics();
            if (selectedUser?.user._id === user._id) {
                await loadDetail(user._id);
            }
        } catch {
            setError('Không thể chặn tài khoản. Vui lòng thử lại.');
        } finally {
            setUnlockingUserId(null);
        }
    };

    const createUser = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsCreatingUser(true);
        setError('');

        const userData: Record<string, string> = {
            username: newUser.username.trim(),
            password: newUser.password,
            role: newUser.role,
        };
        if (newUser.fullName.trim()) userData.fullName = newUser.fullName.trim();
        if (newUser.email.trim()) userData.email = newUser.email.trim();
        if (newUser.phone.trim()) userData.phone = newUser.phone.trim();

        try {
            await apiService.post('/users', userData);
            setIsCreateModalOpen(false);
            setNewUser({ username: '', fullName: '', email: '', phone: '', password: '', role: 'user' });
            await loadStatistics();
        } catch {
            setError('Không thể tạo tài khoản. Vui lòng kiểm tra thông tin và thử lại.');
        } finally {
            setIsCreatingUser(false);
        }
    };

    useEffect(() => {
        void loadStatistics();
    }, [page, search]);

    const formatDate = (value: any) => value ? new Date(value).toLocaleString('vi-VN') : '-';

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6">
            <div className="mx-auto max-w-7xl rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Thống kê người dùng</h2>
                        <p className="mt-1 text-sm text-gray-500">Tổng hợp mã chung sức và thanh toán theo từng tài khoản</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setIsCreateModalOpen(true)}
                            className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                        >
                            Tạo tài khoản
                        </button>
                        <button
                            type="button"
                            onClick={() => void loadStatistics()}
                            disabled={isLoading}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Làm mới
                        </button>
                    </div>
                </div>

                <form className="flex gap-2 border-b border-gray-100 px-4 py-4 sm:px-6" onSubmit={(event) => {
                    event.preventDefault();
                    setPage(1);
                    setSearch(searchInput.trim());
                }}>
                    <input
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder="Tìm theo tên đăng nhập..."
                        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                    <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Tìm kiếm</button>
                </form>

                {error && <p className="px-6 py-4 text-sm text-red-600">{error}</p>}
                {isLoading ? (
                    <p className="px-6 py-8 text-center text-sm text-gray-500">Đang tải thống kê...</p>
                ) : statistics.length === 0 ? (
                    <p className="px-6 py-8 text-center text-sm text-gray-500">Chưa có người dùng.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-[1040px] w-full text-left text-sm">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-6 py-3">Người dùng</th>
                                    <th className="px-4 py-3">Vai trò</th>
                                    <th className="px-4 py-3 text-right">Lượt mã chung sức</th>
                                    <th className="px-4 py-3 text-right">Lượt thanh toán</th>
                                    <th className="px-4 py-3 text-right">Đã thanh toán</th>
                                    <th className="px-6 py-3 text-right">Chưa thanh toán</th>
                                    <th className="px-6 py-3 text-right">Số dư</th>
                                    <th className="px-4 py-3">Trạng thái</th>
                                    <th className="px-6 py-3 text-right">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {statistics.map((item) => (
                                    <tr key={item._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <p className="font-medium text-gray-900">{item.fullName || item.username || '-'}</p>
                                            {item.fullName && <p className="text-xs text-gray-500">{item.username}</p>}
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                                                item.role === 'partner' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                                {roleLabel[item.role] || item.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-right text-gray-700">{item.orderCount}</td>
                                        <td className="px-4 py-4 text-right text-gray-700">{item.paymentCount}</td>
                                        <td className="px-4 py-4 text-right font-medium text-green-600">{formatAmount(item.paidTotal)}</td>
                                        <td className="px-6 py-4 text-right font-medium text-orange-600">{formatAmount(item.unpaidTotal)}</td>
                                        <td className="px-6 py-4 text-right font-medium text-blue-600">{formatAmount(item.balance)}</td>
                                        <td className="px-4 py-4">
                                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                                                item.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                            }`}>
                                                {item.status === 'inactive' ? 'Bị khóa' : 'Hoạt động'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button type="button" onClick={() => void loadDetail(item._id)} className="rounded-lg border border-blue-200 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50">Chi tiết</button>
                                                {item.role !== 'admin' && (
                                                    <>
                                                        {item.status !== 'inactive' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => void blockUser(item)}
                                                                disabled={unlockingUserId === item._id}
                                                                className="rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                            >
                                                                {unlockingUserId === item._id ? 'Đang xử lý...' : 'Chặn'}
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => openWalletAdjustment(item, 'credit')}
                                                            className="rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700"
                                                        >
                                                            Nạp tiền
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => openWalletAdjustment(item, 'debit')}
                                                            className="rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50"
                                                        >
                                                            Thu hồi
                                                        </button>
                                                    </>
                                                )}
                                                {item.role === 'partner' && item.unpaidTotal > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setPartnerPaymentUser(item);
                                                            setPartnerPaymentError('');
                                                            setPartnerPayment({ bankName: '', bankAccountName: '', bankAccount: '', amount: '' });
                                                        }}
                                                        className="rounded-lg bg-orange-600 px-3 py-2 text-xs font-medium text-white hover:bg-orange-700"
                                                    >
                                                        Thanh toán
                                                    </button>
                                                )}
                                                {item.role === 'partner' && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => void managePartnerApiKey(item, 'create')}
                                                            disabled={partnerApiKeyBusyUserId === item._id || item.status === 'inactive'}
                                                            className="rounded-lg border border-purple-200 px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                                                        >
                                                            {partnerApiKeyBusyUserId === item._id ? 'Đang xử lý...' : item.status === 'inactive' ? 'Mở khóa để tạo key' : item.hasApiKey ? 'Tạo lại API key' : 'Tạo API key'}
                                                        </button>
                                                        {item.hasApiKey && (
                                                            <button
                                                                type="button"
                                                                onClick={() => void managePartnerApiKey(item, 'revoke')}
                                                                disabled={partnerApiKeyBusyUserId === item._id}
                                                                className="rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                            >
                                                                Thu hồi key
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                {item.status === 'inactive' && item.role !== 'admin' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => void unlockUser(item)}
                                                        disabled={unlockingUserId === item._id}
                                                        className="rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                                    >
                                                        {unlockingUserId === item._id ? 'Đang mở...' : 'Mở khóa'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!isLoading && statistics.length > 0 && (
                    <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 text-sm text-gray-600">
                        <span>Trang {page} / {totalPages}</span>
                        <div className="flex gap-2">
                            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-40">Trước</button>
                            <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-40">Sau</button>
                        </div>
                    </div>
                )}
            </div>

            {walletAdjustmentUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !isAdjustingWallet && setWalletAdjustmentUser(null)}>
                    <form
                        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
                        onClick={(event) => event.stopPropagation()}
                        onSubmit={adjustUserWallet}
                    >
                        <h3 className="text-lg font-semibold text-gray-900">
                            {walletAdjustmentAction === 'credit' ? 'Nạp tiền vào ví' : 'Thu hồi tiền trong ví'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                            {walletAdjustmentUser.fullName || walletAdjustmentUser.username} · Số dư hiện tại {formatAmount(walletAdjustmentUser.balance)}.
                        </p>
                        <label className="mt-4 block text-sm font-medium text-gray-700">
                            Số tiền
                            <input
                                required
                                type="number"
                                min="1"
                                step="1"
                                value={walletAdjustmentAmount}
                                onChange={(event) => setWalletAdjustmentAmount(event.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500"
                                placeholder="Nhập số tiền (đ)"
                            />
                        </label>
                        <label className="mt-4 block text-sm font-medium text-gray-700">
                            Lý do
                            <textarea
                                required
                                minLength={5}
                                maxLength={500}
                                rows={3}
                                value={walletAdjustmentReason}
                                onChange={(event) => setWalletAdjustmentReason(event.target.value)}
                                className="mt-1 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500"
                                placeholder="Nhập lý do điều chỉnh (5-500 ký tự)"
                            />
                        </label>
                        {walletAdjustmentError && <p role="alert" className="mt-3 text-sm text-red-600">{walletAdjustmentError}</p>}
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setWalletAdjustmentUser(null)}
                                disabled={isAdjustingWallet}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={isAdjustingWallet}
                                className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${walletAdjustmentAction === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                            >
                                {isAdjustingWallet ? 'Đang xử lý...' : walletAdjustmentAction === 'credit' ? 'Xác nhận nạp' : 'Xác nhận thu hồi'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {partnerPaymentUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !isRecordingPartnerPayment && setPartnerPaymentUser(null)}>
                    <form className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()} onSubmit={recordPartnerPayment}>
                        <div className="mb-5">
                            <h3 className="text-lg font-semibold text-gray-900">Ghi nhận thanh toán công nợ</h3>
                            <p className="mt-1 text-sm text-gray-600">
                                {partnerPaymentUser.fullName || partnerPaymentUser.username} · Còn nợ {formatAmount(partnerPaymentUser.unpaidTotal)}.
                                Xác nhận sẽ đánh dấu công nợ hiện tại đã thanh toán.
                            </p>
                        </div>
                        <div className="grid gap-4">
                            <label className="text-sm font-medium text-gray-700">
                                Tên ngân hàng
                                <input required value={partnerPayment.bankName} onChange={(event) => setPartnerPayment({ ...partnerPayment, bankName: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Tên chủ tài khoản
                                <input required value={partnerPayment.bankAccountName} onChange={(event) => setPartnerPayment({ ...partnerPayment, bankAccountName: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Số tài khoản
                                <input required value={partnerPayment.bankAccount} onChange={(event) => setPartnerPayment({ ...partnerPayment, bankAccount: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Số tiền thực nhận (không bắt buộc)
                                <input type="number" min="1" step="1" value={partnerPayment.amount} onChange={(event) => setPartnerPayment({ ...partnerPayment, amount: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                        </div>
                        {partnerPaymentError && <p role="alert" className="mt-4 text-sm text-red-600">{partnerPaymentError}</p>}
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" onClick={() => setPartnerPaymentUser(null)} disabled={isRecordingPartnerPayment} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Hủy</button>
                            <button type="submit" disabled={isRecordingPartnerPayment} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50">{isRecordingPartnerPayment ? 'Đang ghi nhận...' : 'Xác nhận thanh toán'}</button>
                        </div>
                    </form>
                </div>
            )}

            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !isCreatingUser && setIsCreateModalOpen(false)}>
                    <form className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()} onSubmit={createUser}>
                        <div className="mb-5 flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Tạo tài khoản</h3>
                                <p className="mt-1 text-sm text-gray-500">Nhập thông tin đăng nhập và phân quyền.</p>
                            </div>
                            <button type="button" onClick={() => setIsCreateModalOpen(false)} disabled={isCreatingUser} className="text-xl text-gray-500 disabled:opacity-50" aria-label="Đóng">×</button>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <label className="text-sm font-medium text-gray-700">
                                Username <span className="text-red-600">*</span>
                                <input required name="username" value={newUser.username} onChange={(event) => setNewUser({ ...newUser, username: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Mật khẩu <span className="text-red-600">*</span>
                                <input required minLength={6} type="password" name="password" value={newUser.password} onChange={(event) => setNewUser({ ...newUser, password: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Họ tên
                                <input name="fullName" value={newUser.fullName} onChange={(event) => setNewUser({ ...newUser, fullName: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Email
                                <input type="email" name="email" value={newUser.email} onChange={(event) => setNewUser({ ...newUser, email: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Số điện thoại
                                <input type="tel" name="phone" value={newUser.phone} onChange={(event) => setNewUser({ ...newUser, phone: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-blue-500" />
                            </label>
                            <label className="text-sm font-medium text-gray-700">
                                Vai trò
                                <select value={newUser.role} onChange={(event) => setNewUser({ ...newUser, role: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500">
                                    <option value="user">Thành viên</option>
                                    <option value="partner">Đối tác</option>
                                    <option value="admin">Quản trị viên</option>
                                </select>
                            </label>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" onClick={() => setIsCreateModalOpen(false)} disabled={isCreatingUser} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Hủy</button>
                            <button type="submit" disabled={isCreatingUser} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">{isCreatingUser ? 'Đang tạo...' : 'Tạo tài khoản'}</button>
                        </div>
                    </form>
                </div>
            )}

            {(selectedUser || isDetailLoading) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !isDetailLoading && setSelectedUser(null)}>
                    <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
                        {isDetailLoading ? <p className="py-8 text-center text-sm text-gray-500">Đang tải chi tiết...</p> : selectedUser && <>
                            <div className="flex items-start justify-between gap-4">
                                <div><h3 className="text-xl font-semibold text-gray-900">{selectedUser.user.fullName || selectedUser.user.username}</h3><p className="text-sm text-gray-500">@{selectedUser.user.username} · {selectedUser.user.role}</p></div>
                                <button type="button" onClick={() => setSelectedUser(null)} className="text-xl text-gray-500">×</button>
                            </div>
                            <div className="mt-4 grid gap-2 rounded-lg bg-gray-50 p-4 text-sm text-gray-700 sm:grid-cols-2 lg:grid-cols-4">
                                <span>Email: {selectedUser.user.email || '-'}</span>
                                <span>SĐT: {selectedUser.user.phone || '-'}</span>
                                <span>Trạng thái: {statusLabel[selectedUser.user.status || ''] || selectedUser.user.status || '-'}</span>
                                <span>Số dư ví: <strong className="text-blue-700">{formatAmount(selectedUser.balance)}</strong></span>
                            </div>
                            <h4 className="mt-6 mb-2 font-semibold text-gray-900">Lịch sử đăng nhập ({selectedUser.user.loginHistory?.length || 0})</h4>
                            <div className="overflow-x-auto"><table className="min-w-full text-left text-sm"><thead className="bg-gray-50"><tr><th className="px-3 py-2">Thời gian</th><th className="px-3 py-2">Kết quả</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">Thiết bị / trình duyệt</th></tr></thead><tbody className="divide-y">{selectedUser.user.loginHistory?.length ? selectedUser.user.loginHistory.slice().reverse().map((entry, index) => <tr key={`${entry.loggedAt}-${index}`}><td className="whitespace-nowrap px-3 py-2">{formatDate(entry.loggedAt)}</td><td className={`whitespace-nowrap px-3 py-2 font-medium ${entry.success ? 'text-green-700' : 'text-red-700'}`}>{entry.success ? 'Thành công' : 'Thất bại'}</td><td className="whitespace-nowrap px-3 py-2 font-mono">{entry.ipAddress}</td><td className="max-w-md break-all px-3 py-2 text-xs text-gray-600">{entry.userAgent}</td></tr>) : <tr><td className="px-3 py-3 text-gray-500" colSpan={4}>Chưa có dữ liệu đăng nhập.</td></tr>}</tbody></table></div>
                            <h4 className="mt-6 mb-2 font-semibold text-gray-900">Lịch sử order ({selectedUser.orders.length})</h4>
                            <div className="overflow-x-auto"><table className="min-w-full text-left text-sm"><thead className="bg-gray-50"><tr><th className="px-3 py-2">Mã chung sức</th><th className="px-3 py-2">Trạng thái</th><th className="px-3 py-2 text-right">Đã trả</th><th className="px-3 py-2 text-right">Còn thiếu</th><th className="px-3 py-2">Ngày tạo</th></tr></thead><tbody className="divide-y">{selectedUser.orders.map((order) => <tr key={String(order._id)}><td className="px-3 py-2">{order.code || '-'}</td><td className="px-3 py-2">{statusLabel[order.status] || order.status || '-'}</td><td className="px-3 py-2 text-right">{formatAmount(order.paidAmount)}</td><td className="px-3 py-2 text-right">{formatAmount(order.unpaidAmount)}</td><td className="px-3 py-2">{formatDate(order.createdAt)}</td></tr>)}</tbody></table></div>
                            <h4 className="mt-6 mb-2 font-semibold text-gray-900">Lịch sử giao dịch ({selectedUser.transactions.length})</h4>
                            <div className="overflow-x-auto"><table className="min-w-full text-left text-sm"><thead className="bg-gray-50"><tr><th className="px-3 py-2">Mã giao dịch</th><th className="px-3 py-2">Loại</th><th className="px-3 py-2">Trạng thái</th><th className="px-3 py-2 text-right">Số tiền</th><th className="px-3 py-2">Thông tin ngân hàng</th><th className="px-3 py-2">Ngày tạo</th></tr></thead><tbody className="divide-y">{selectedUser.transactions.map((transaction) => <tr key={String(transaction._id)}><td className="px-3 py-2">{transaction.transactionId || '-'}</td><td className="px-3 py-2">{transactionTypeLabel[transaction.type] || transaction.type || '-'}</td><td className="px-3 py-2">{statusLabel[transaction.status] || transaction.status || '-'}</td><td className="px-3 py-2 text-right">{transaction.amount > 0 ? formatAmount(transaction.amount) : 'Không ghi nhận'}</td><td className="px-3 py-2">{transaction.bankName || transaction.bankCode || '-'}<br />{transaction.bankAccountName || '-'} · {transaction.bankAccount || '-'}</td><td className="px-3 py-2">{formatDate(transaction.createdAt)}</td></tr>)}</tbody></table></div>
                        </>}
                    </div>
                </div>
            )}

            {newPartnerApiKey && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setNewPartnerApiKey(null)}>
                    <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-900">API key Partner đã tạo</h3>
                        <p className="mt-2 text-sm text-amber-700">Hãy sao chép và lưu lại ngay. Key thô chỉ được hiển thị một lần; tạo lại key sẽ vô hiệu hóa key cũ.</p>
                        <code className="mt-4 block break-all rounded-lg bg-gray-100 p-3 text-sm text-gray-800">{newPartnerApiKey}</code>
                        <p className="mt-3 text-sm text-gray-600">Gửi key qua header <code>x-api-key</code> khi gọi API.</p>
                        <div className="mt-5 flex justify-end">
                            <button type="button" onClick={() => setNewPartnerApiKey(null)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Đã lưu key</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUserStatisticsPage;
